import { makeStyles } from '@material-ui/styles'
import { IPVersions } from 'app/constants'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import OptionsToggler from 'core/components/OptionsToggler'
import PaginatedSelectableCards from 'core/components/PaginatedSelectableCards'
import SelectableCard from 'core/components/SelectableCard'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import Progress from 'core/components/progress/Progress'
import CodeMirror from 'core/components/validatedForm/CodeMirrorField'
import {
  default as DropdownField,
  default as PicklistField,
} from 'core/components/validatedForm/DropdownField'
import Info from 'core/components/validatedForm/Info'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Slider from 'core/elements/Slider'
import Text from 'core/elements/Text'
import Dropdown from 'core/elements/dropdown'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import {
  customValidator,
  nonDecimalValueValidator,
  requiredValidator,
  validIpv4Validator,
  yamlValidator,
} from 'core/utils/fieldValidators'
import { isHotpluggableFlavor, isOpenCloud } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import * as IpAddress from 'ip-address'
import { isSelfServiceRole } from 'k8s/util/helpers'
import { humanReadableSize } from 'openstack/helpers'
import { ascend, prop, sortWith } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { noop, stopPropagation } from 'utils/fp'
import { durationBetweenDates, formatDate } from 'utils/misc'
import ServiceUnhealthyInfo from '../common/ServiceUnhealthyInfo'
import { listFlavors } from '../flavors/actions'
import { flavorsSelector } from '../flavors/selectors'
import { listImages } from '../images/actions'
import { imagesSelector } from '../images/selectors'
import { listHostAggregates, listResmgrHosts } from '../infrastructure/actions'
import ClusterHostGroupsPicklist from '../infrastructure/clusters/ClusterHostGroupsPicklist'
import { imageLibrayHostSelector, resmgrHostsSelector } from '../infrastructure/selectors'
import { listNetworkAvailability, listNetworks } from '../networks/actions'
import { listSecurityGroups } from '../networks/security-groups/actions'
import { securityGroupsSelector } from '../networks/security-groups/selectors'
import { networksSelector } from '../networks/selectors'
import { listVolumeSnapshots } from '../storage/snapshots/actions'
import {
  volumeSnapshotsByIdSelector,
  volumeSnapshotsSelector,
} from '../storage/snapshots/selectors'
import VolumeTypesPicklist from '../storage/volume-types/VolumeTypesPicklist'
import { listVolumes } from '../storage/volumes/actions'
import { volumesByIdSelector, volumesSelector } from '../storage/volumes/selectors'
import { listClusterHostGroups } from 'openstack/components/infrastructure/clusters/actions'
import { clusterHostGroupsSelector } from 'openstack/components/infrastructure/clusters/selectors'
import MigrationPriorityPicklist from './MigrationPriorityPicklist'
import SourceTypesPicklist from './SourceTypesPicklist'
import SshKeyPicklist from './SshKeyPicklist'
import { IVirtualMachinesListPageTabs } from './VirtualMachinesListPage'
import { createVirtualMachine, createVirtualMachines, listVirtualMachinesLite } from './actions'
import {
  flavorColumns,
  getVmSnapshotColumns,
  isFlavorCompatibleWithImageAndVolume,
  isImageCompatibleWithVolumeAndFlavor,
  securityGroupColumns,
  visibleImageColumns,
  visibleVmSnapshotColumns,
  volumeColumns,
} from './helpers'
import { virtualMachinesLiteByIdSelector } from './selectors'
import ServerGroupsPicklist from './server-groups/ServerGroupsPicklist'
import { imageColumns } from '../images/ImagesListPage'
import { getValidVolumeTypes } from '../storage/volumes/helpers'
import VmCredentialsPanel from './VmCredentialsPanel'
import { VOLUME_BACKED_VM_WARNING_MSG } from './tooltipConstants'
import clsx from 'clsx'
import { trackEvent } from 'utils/tracking'

const AVAILABLITY_ZONE_ERROR = 'The requested availability zone is not available'

const modalTitle = 'Deploy New VM'

const returnStepNumber = (source, step) =>
  !['image', 'snapshot'].includes(source) ? step : step - 1

export const placeholderCloudInitYaml = `#cloud-config
password: winterwonderland
chpasswd: { expire: False }
ssh_pwauth: True
manage_etc_hosts: true
runcmd:
- [ 'sh', '-c', 'echo "Hello World" > /tmp/helloworld.txt']`

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    display: 'grid',
    gap: 16,
    marginBottom: 32,
    height: 'fit-content',
  },
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
  info: {
    background: theme.palette.grey[50],
  },
  infoContent: {
    display: 'grid',
    gap: 8,
  },
  halfColumns: {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'baseline',
  },
  errorMessage: {
    display: 'grid',
    gridTemplateColumns: '26px auto',
    alignItems: 'center',
  },
  errorText: {
    color: theme.components.graph.error,
  },
  spaceBelow: {
    marginBottom: 16,
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gap: 16,
  },
  card: {
    height: 'calc(100% - 32px)',
    alignItems: 'center',
    padding: '16px 24px',
  },
  cardHeader: {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    paddingBottom: 8,
    marginBottom: 8,
    display: 'grid',
    gridTemplateColumns: 'auto max-content',
    alignItems: 'center',
  },
  gpuTag: {
    background: theme.palette.blue[100],
    border: `1px solid ${theme.palette.blue[700]}`,
    borderRadius: 4,
    padding: '2px 8px',
    color: theme.palette.blue[700],
    fontWeight: 600,
  },
  cardText: {
    fontSize: 12,
  },
  pairs: {
    display: 'grid',
    gap: 4,
  },
  separator: {
    height: 1,
    width: '100%',
    background: theme.palette.grey[200],
    margin: '16px 0px',
  },
  vmCount: {
    display: 'grid',
    gridTemplateColumns: 'auto 130px',
    gap: 16,
    alignItems: 'center',
  },
  vmCountDisabled: {
    cursor: 'not-allowed',
  },
  vmCountInput: {
    display: 'grid',
    gridTemplateColumns: 'auto max-content',
    alignItems: 'center',
    gap: 8,
  },
  vmCountText: {
    fontSize: 12,
  },
  tabbed: {
    marginLeft: 52,
  },
  passwordScreen: {
    display: 'grid',
    gap: 24,
    height: 'fit-content',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gap: 16,
  },
  credentials: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '1fr 6fr',
    alignItems: 'center',
  },
  warning: {
    border: `1px solid ${theme.palette.yellow[500]}`,
    background: theme.palette.yellow[100],
  },
}))

interface Props {
  addRoute: Route
  reloadVirtualMachines: () => void
}

export const getPrivateIpValidator = (network) => {
  return customValidator((value, formValues) => {
    if (!value) {
      return true
    }
    try {
      const subnets = network?.subnetDetails
      const pass = subnets?.some((subnet) => {
        const testCidr =
          subnet?.ip_version === IPVersions.IPv4
            ? new IpAddress.Address4(subnet?.cidr)
            : new IpAddress.Address6(subnet?.cidr)
        return subnet?.ip_version === IPVersions.IPv4
          ? new IpAddress.Address4(value).isInSubnet(testCidr)
          : new IpAddress.Address6(value).isInSubnet(testCidr)
      })
      return pass
    } catch (err) {
      return false
    }
  }, 'IP Address must be within one of the subnet CIDRs')
}

const countValidator = customValidator((value) => {
  return value >= 1 && value <= 20
}, 'Count must be between 1 and 20')

export const hotplugMemoryValidator = customValidator((value, formValues) => {
  const max = Number(formValues.hotplugMemoryMax)
  return value <= max && value > 0
}, 'Value must be less than or equal to the hotplug memory maximum and greater than 0')

export const hotplugVcpuValidator = customValidator((value, formValues) => {
  const max = Number(formValues.hotplugVcpuMax)
  return value <= max && value > 0
}, 'Value must be less than or equal to the hotplug vCPU maximum and greater than 0')

export const hotplugVcpuMaxValidator = customValidator((value) => {
  return value <= 255
}, 'Value can at most be 255')

export const ErrorText = ({ children }) => {
  const classes = useStyles({})
  return (
    <div className={classes.errorMessage}>
      <FontAwesomeIcon>circle-exclamation</FontAwesomeIcon>
      <Text variant="body1" className={classes.errorText}>
        {children}
      </Text>
    </div>
  )
}

const SubnetsCellComponent = ({ value }) => {
  return (
    <>
      {value.map((subnet) => (
        <Text key={subnet.name} variant="body2">
          {subnet.name}: <b>{subnet.cidr}</b>
        </Text>
      ))}
    </>
  )
}

export default function CreateVirtualMachineModal({ addRoute, reloadVirtualMachines }: Props) {
  const { history, match } = useReactRouter()
  const classes = useStyles()
  const [viewImageToggle, setViewImageToggle] = useState('list')
  const [viewFlavorToggle, setViewFlavorToggle] = useState('list')
  const [imageSort, setImageSort] = useState('')
  const [flavorSort, setFlavorSort] = useState('')
  const [showVmPassword, setShowVmPassword] = useState(false)
  const [vmPassword, setVmPassword] = useState('')
  const [imageType, setImageType] = useState('')
  const [minAvailableIpsNetwork, setMinAvailableIpsNetwork] = useState(null)
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'

  const defaultParams = {
    name: '',
    sourceType: 'image',
    image: [],
    attachVolumes: [],
    volume: [],
    volumeSize: '20',
    volumeType: '',
    volumeSnapshot: [],
    flavor: [],
    deleteOnTermination: true,
    availabilityZone: null,
    enableHotplugging: false,
    gpuCompatible: false,
    networks: [],
    selectedPorts: {},
    fixedIPs: {},
    count: 1,
    sshKey: null,
    serverGroup: null,
    useCloudInit: false,
    useSecurityGroups: false,
    cloudInitYaml: placeholderCloudInitYaml,
    securityGroups: [],
    metadata: [],
    hotplugMemory: null,
    hotplugMemoryMax: 65536,
    hotplugVcpu: null,
    hotplugVcpuMax: 64,
    priority: '',
  }
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)
  const isSelfServiceUser = isSelfServiceRole(session)

  const { loading: loadingImages } = useListAction(listImages, {
    params: {},
  })
  const images = useSelector(imagesSelector)

  const { loading: loadingVolumes } = useListAction(listVolumes, {
    params: {},
  })
  const volumes = useSelector(volumesSelector)
  const volumesList = volumes.filter(
    (volume) => volume.status === 'available' && volume.bootable !== 'false',
  )
  const volumesById = useSelector(volumesByIdSelector)

  const { loading: loadingFlavors } = useListAction(listFlavors, {
    params: {},
  })
  const flavors = useSelector(flavorsSelector)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots, {
    params: {},
  })
  const volumeSnapshots = useSelector(volumeSnapshotsSelector)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)

  const { loading: loadingNetworks } = useListAction(listNetworks, {
    params: {},
    initialLoadingState: true, // for the useEffect that is tracking loading
  })
  const _networksList = useSelector(networksSelector)
  // Filter networks based on subnet, shared, tenant_id and isSelfServiceUser
  // If network has subnets and is shared, then show it
  // If network has subnets and is not shared, then show it based on below conditions
  // For networks in current tenant, if user is self service, then only show networks that are not external
  // For networks in current tenant, if user is not self service, then show all networks
  // For networks not in current tenant, then hide those networks
  const networksList = _networksList.filter(
    (network) =>
      !!network.subnets?.length &&
      (network.shared ||
        (network.tenant_id === currentTenant
          ? isSelfServiceUser
            ? !network?.['router:external']
            : true
          : false)),
  )

  const { loading: loadingNetworkAvailability, reload: reloadNetworkAvailability } = useListAction(
    listNetworkAvailability,
  )

  const { loading: loadingSecurityGroups } = useListAction(listSecurityGroups, {
    params: {},
  })
  const securityGroupsList = useSelector(securityGroupsSelector)

  // Check if image library host is available in the host list
  const { loading: loadingResmgrHosts } = useListAction(listResmgrHosts, {
    params,
  })
  const imageLibraryHost = useSelector(imageLibrayHostSelector)
  const isImageLibraryHostAvailable = !!imageLibraryHost

  const { loading: loadingVms } = useListAction(listVirtualMachinesLite)
  const vmsById = useSelector(virtualMachinesLiteByIdSelector)

  const { loading: loadingHostAggregates } = useListAction(listHostAggregates)

  const { update, updating, error, reset } = useUpdateAction(createVirtualMachine)
  const {
    update: createMultiple,
    updating: creatingMultiple,
    error: errorMultiple,
    reset: resetMultiple,
  } = useUpdateAction(createVirtualMachines)
  const responseError = error || errorMultiple

  useEffect(() => {
    reloadNetworkAvailability(true)
  }, [])

  // Check if modal is open
  const isOpen = addRoute.pattern.match(match.url)

  const { loading: loadingClusterHostGroups } = useListAction(listClusterHostGroups)
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    // After everything is loaded, track object numbers
    if (
      !loadingImages &&
      !loadingVolumes &&
      !loadingFlavors &&
      !loadingNetworks &&
      !loadingNetworkAvailability &&
      !loadingClusterHostGroups
    ) {
      const body = {
        images: images?.length,
        volumes: volumes?.length,
        flavors: flavors?.length,
        networks: networksList?.length,
        networksWithFreeIps: networksList?.filter((network) => network?.availableIps > 0)?.length,
        clusters: clusterHostGroups?.length,
      }
      trackEvent(`${modalTitle} - Resources Present`, body)
    }
  }, [
    loadingImages,
    loadingVolumes,
    loadingFlavors,
    loadingNetworks,
    loadingNetworkAvailability,
    loadingClusterHostGroups,
    match.url,
  ])

  // Return only the volumes with status available or not selected in existing volume
  const availableVolumes = useMemo(
    () =>
      volumes?.filter((volume) => {
        if (volume.status !== 'available') return false
        if (params.sourceType !== 'existingVolume') return true
        return !params.volume.includes(volume)
      }),
    [volumes, params.sourceType, params.volume],
  )

  useEffect(() => {
    if (params?.sourceType === 'existingVolume' || params.attachVolumes.length > 0) {
      updateParams({ count: 1 })
    }
  }, [params.sourceType, params.attachVolumes])

  const filteredImages = useMemo(() => {
    const filtered = images.filter((image) => {
      if (image.status !== 'active') {
        return false
      }
      if (params.sourceType === 'snapshot') {
        if (imageType === 'VM Ephemeral Disk Snapshot') {
          return image?.image_type === 'snapshot'
        } else if (imageType === 'VM Volume Snapshot') {
          return !!image?.block_device_mapping
        }
        return image?.image_type === 'snapshot' || image?.block_device_mapping
      }
      return !(image?.image_type === 'snapshot' || image?.block_device_mapping)
    })
    const sorter = {
      name: sortWith([ascend(prop('name'))]),
      virtualSize: sortWith([ascend(prop('virtual_size'))]),
    }[imageSort]
    return imageSort ? sorter(filtered) : filtered
  }, [images, imageSort, params.sourceType, imageType])

  const submitForm = useCallback(async () => {
    // We should consider the smallest number of available IPs
    // from all the selected networks. For example, if 5 networks are selected with 1 available IP each,
    // the limiting factor is the smallest available IP (1), so we can only create 1 VM in this case.
    const networkWithMinIps = params?.networks?.reduce((minNet, currentNet) => {
      return currentNet?.availablePortAndIPs < minNet?.availablePortAndIPs ? currentNet : minNet
    })
    setMinAvailableIpsNetwork(networkWithMinIps)
    // If the smallest available IPs across networks is less than the VM count, return early
    if (networkWithMinIps?.availablePortAndIPs < params.count) {
      return
    }

    // not sure why encode/decode is needed
    const encodedScript = params.useCloudInit ? btoa(params.cloudInitYaml) : undefined
    const securityGroups = params.useSecurityGroups
      ? params.securityGroups.map((group) => ({
          name: group.name,
        }))
      : undefined
    const metadata = params.metadata.reduce((accum, pair) => {
      return {
        ...accum,
        [pair.key]: pair.value,
      }
    }, {})
    const hotplugMetadata = params.enableHotplugging
      ? {
          HOTPLUG_MEMORY: String(params.hotplugMemory),
          HOTPLUG_MEMORY_MAX: String(params.hotplugMemoryMax),
          HOTPLUG_CPU: String(params.hotplugVcpu),
          HOTPLUG_CPU_MAX: String(params.hotplugVcpuMax),
        }
      : {}
    const combinedMetadata = {
      ...metadata,
      ...hotplugMetadata,
      ...(params.priority !== '' && { 'migration-priority': params.priority }),
    }

    const additionalVolumes = params.attachVolumes.map((volume) => ({
      uuid: volume?.id,
      source_type: 'volume',
      destination_type: 'volume',
      boot_index: -1,
      delete_on_termination: false,
    }))

    const body = {
      server: {
        imageRef: ['image', 'snapshot'].includes(params.sourceType)
          ? params.image?.[0]?.id
          : undefined,
        block_device_mapping_v2: ['image', 'snapshot'].includes(params.sourceType)
          ? undefined
          : [
              ...(params.sourceType === 'existingVolume'
                ? [
                    {
                      source_type: 'volume',
                      destination_type: 'volume',
                      boot_index: 0,
                      delete_on_termination: false,
                      uuid: params.volume[0]?.id,
                    },
                  ]
                : params.sourceType === 'newVolume'
                ? [
                    {
                      source_type: 'image',
                      destination_type: 'volume',
                      boot_index: 0,
                      delete_on_termination: params.deleteOnTermination,
                      uuid: params.image?.[0]?.id,
                      volume_size: params.volumeSize,
                      volume_type: params.volumeType ? params.volumeType : undefined,
                    },
                  ]
                : params.sourceType === 'volumeSnapshot'
                ? [
                    {
                      source_type: 'snapshot',
                      destination_type: 'volume',
                      boot_index: 0,
                      delete_on_termination: params.deleteOnTermination,
                      uuid: params.volumeSnapshot?.[0]?.id,
                    },
                  ]
                : []),
              ...additionalVolumes,
            ],
        flavorRef: params.flavor?.[0]?.id,
        name: params.name,
        networks: params.networks.map((network) => ({
          uuid: network.id,
          // When port is selected as fixedIP, use fixedIP
          // When any other port is selected, use portId
          ...(params.selectedPorts[network.id]
            ? params.selectedPorts[network.id] === 'fixedIP'
              ? {
                  fixed_ip: params.fixedIPs[network.id],
                }
              : { port: params.selectedPorts[network.id] }
            : {}),
        })),
        key_name: params.sshKey
          ? params.sshKey === 'None'
            ? undefined
            : params.sshKey
          : undefined,
        config_drive: false, // Maybe could just remove this line?
        user_data: params.useCloudInit ? encodedScript : undefined,
        security_groups: securityGroups,
        metadata: combinedMetadata,
        ...(params.availabilityZone && { availability_zone: params.availabilityZone }),
        min_count: params.count === 1 ? undefined : params.count,
        return_reservation_id: params.count === 1 ? undefined : true,
      },
      'OS-SCH-HNT:scheduler_hints': params.serverGroup
        ? {
            group: params.serverGroup?.id,
          }
        : undefined,
    }
    const updateFn = params.count === 1 ? update : createMultiple
    const { success, response } = await updateFn({ body })
    if (success) {
      trackEvent(`${modalTitle} - Finished`)
      reloadVirtualMachines()
      if (params.count > 1) {
        handleClose()
        return
      }
      setVmPassword(response?.adminPass)
      setShowVmPassword(true)
    }
  }, [params])

  const handleClose = () => {
    history.push(
      routeManager.routes.openstack.vms.path({
        vmstab: IVirtualMachinesListPageTabs.VirtualMachinesList,
      }),
    )
    setShowVmPassword(false)
    setVmPassword('')
    reset()
    resetMultiple()
    setImageSort('')
    setFlavorSort('')
    setParams(defaultParams)
  }

  // If sourceType is newVolume, volumeSnapshot or existingVolume, only show flavors with 0 disk
  // If sourceType is snapshot and imageType is vmVolumeSnapshot, only show flavors with 0 disk
  const isVolumeSourceType = useMemo(
    () =>
      ['newVolume', 'existingVolume', 'volumeSnapshot'].includes(params.sourceType) ||
      (['snapshot'].includes(params.sourceType) &&
        params.image?.[0]?.imageType === 'vmVolumeSnapshot'),
    [params.sourceType, params.image],
  )

  const filteredFlavors = useMemo(() => {
    const filtered = flavors.filter((flavor) => {
      const isHotpluggable = isHotpluggableFlavor(flavor)
      if (params.enableHotplugging !== isHotpluggable) {
        return false
      }
      if (flavor?.gpuFlavor !== params.gpuCompatible) return false
      return true
    })
    const filteredFlavorsForVolume = filtered?.filter((flavor) => {
      // If sourceType is newVolume or existingVolume, only show flavors with 0 disk
      if (isVolumeSourceType) return flavor?.disk === 0
      return true
    })
    const sorter = {
      name: sortWith([ascend(prop('name'))]),
      vcpus: sortWith([ascend(prop('vcpus'))]),
      ram: sortWith([ascend(prop('ram'))]),
      disk: sortWith([ascend(prop('disk'))]),
    }[flavorSort]
    return flavorSort ? sorter(filteredFlavorsForVolume) : filteredFlavorsForVolume
  }, [flavors, flavorSort, params.enableHotplugging, params.gpuCompatible, isVolumeSourceType])

  const renderFlavorCard = useMemo(
    () => (item) => {
      // Flavor must be larger than the image size
      const disabled = isFlavorCompatibleWithImageAndVolume(item, params)
      return (
        <SelectableCard
          id={item}
          onClick={(item) => updateParams({ flavor: [item] })}
          tooltip={disabled ? 'Flavor disk size is smaller than the selected image' : undefined}
          active={params.flavor?.[0]?.id === item.id}
          className={classes.card}
          disabled={disabled}
          withCustomBody
          showRadioButton
        >
          <Text variant="body2" className={classes.cardHeader}>
            <b>{item.name}</b>
            {item?.gpuFlavor && <div className={classes.gpuTag}>GPU</div>}
          </Text>
          <div className={classes.pairs}>
            <Text variant="body2" className={classes.cardText}>
              vCPUs: <b>{item?.vcpus}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              RAM: <b>{item?.ram && humanReadableSize(item?.ram * 1024 * 1024)}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              Disk: <b>{item?.disk && humanReadableSize(item?.disk * 1024 * 1024 * 1024)}</b>
              {disabled && ' (Too Small)'}
            </Text>
          </div>
        </SelectableCard>
      )
    },
    [params.image, params.flavor, params.sourceType],
  )

  const renderImageCard = useMemo(
    () => (item) => {
      const disabled = isImageCompatibleWithVolumeAndFlavor(item, params)
      return (
        <SelectableCard
          id={item}
          onClick={(item) => updateParams({ image: [item] })}
          tooltip={
            disabled
              ? `Image is not compatible with the selected flavor and volume size.`
              : undefined
          }
          active={params.image?.[0]?.id === item.id}
          className={classes.card}
          disabled={disabled}
          withCustomBody
          showRadioButton
        >
          <Text variant="body2" className={classes.cardHeader}>
            <b>{item.name}</b>
          </Text>
          <div className={classes.pairs}>
            <Text variant="body2" className={classes.cardText}>
              Format: <b>{item?.disk_format}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              Virtual Size:{' '}
              <b>
                {item?.virtual_size ? humanReadableSize(item?.virtual_size) : 'N/A'}
                {disabled && ' (Too Large)'}
              </b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              Age:{' '}
              <b>{item?.created_at && durationBetweenDates({ labels: ['d'] })(item?.created_at)}</b>
            </Text>
          </div>
        </SelectableCard>
      )
    },
    [params.image, params.volumeSize, params.sourceType, params.flavor],
  )

  const networkColumns = [
    {
      id: 'name',
      label: 'Name',
    },
    {
      id: 'subnetDetails',
      label: 'Subnets',
      render: (value) => <SubnetsCellComponent value={value} />,
    },
    {
      id: 'ports',
      label: 'Ports',
      Component: ({ row, isSelected }) => {
        const classes = useStyles()
        const options = row?.availablePorts?.length
          ? [
              // If there are available IP addresses, show the 'Automatic' and 'Private IP' options
              ...(row?.availableIps > 0
                ? [
                    { label: 'Automatic', value: '' },
                    { label: 'Private IP', value: 'fixedIP' },
                  ]
                : [
                    {
                      label: 'Select a port (No available IPs)',
                      value: '',
                    },
                  ]),
              // If there are available ports, show the ports as options
              ...row?.availablePorts.map((port) => {
                const ipAddressString = port.fixed_ips?.map((ip) => ip.ip_address)?.join(', ')
                return {
                  label: `${ipAddressString} (${port.name || port.id})`,
                  value: port.id,
                }
              }),
            ]
          : [
              { label: 'Automatic', value: '' },
              { label: 'Private IP', value: 'fixedIP' },
            ]
        return (
          <div className={classes.portSelection} onClick={isSelected ? stopPropagation : noop}>
            <DropdownField
              id={`${row.id}-ports`}
              DropdownComponent={Dropdown}
              name="ports"
              value={params.selectedPorts[row.id] || ''}
              items={options}
              onChange={(value) =>
                getParamsUpdater('selectedPorts')({
                  ...params.selectedPorts,
                  [row.id]: value,
                })
              }
              required={row?.availableIps === 0 && isSelected}
            />
          </div>
        )
      },
    },
    {
      id: 'fixedIP',
      label: 'Private IP',
      Component: ({ row, isSelected }) => {
        const [fixedIP, setFixedIP] = useState(params.fixedIPs[row.id])
        const isRequired = params.selectedPorts[row.id] === 'fixedIP'
        const selectedPort = row?.ports?.find((port) => port.id === params.selectedPorts[row.id])
        return isRequired ? (
          <div onClick={isSelected ? stopPropagation : noop}>
            <TextField
              id={`${row.id}-fixedIP`}
              placeholder="Enter private IP"
              className={classes.fixedIPInput}
              value={fixedIP}
              onChange={setFixedIP}
              onBlur={() => {
                getParamsUpdater('fixedIPs')({ ...params.fixedIPs, [row.id]: fixedIP })
              }}
              disabled={!isRequired}
              validations={
                isRequired && isSelected
                  ? [validIpv4Validator, requiredValidator, getPrivateIpValidator(row)]
                  : null
              }
            />
          </div>
        ) : selectedPort ? (
          <div>
            {selectedPort?.fixed_ips?.map((ip) => (
              <Text variant="body2">{ip.ip_address}</Text>
            ))}
          </div>
        ) : null
      },
    },
  ]

  const vmSnapshotColumns = useMemo(() => {
    return getVmSnapshotColumns({ vmsById, volumeSnapshotsById })
  }, [vmsById, volumeSnapshotsById])

  const imageColumnsLocal = useMemo(() => {
    return imageColumns(false).map((col) => ({ ...col, id: col.key }))
  }, [imageColumns])

  const volumeSnapshotColumns = useMemo(
    () => [
      {
        id: 'name',
        label: 'name',
      },
      {
        id: 'description',
        label: 'Description',
      },
      {
        id: 'status',
        label: 'Status',
      },
      {
        id: 'size',
        label: 'Size',
        render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
      },
      {
        id: 'volumeType',
        label: (
          <TitleWithInfoIcon
            titleText="Volume Type"
            tooltipMsg={<div>This information is inherited from the source volume</div>}
          />
        ),
        render: (_, item) => {
          const volume = volumesById?.[item?.volume_id]
          return volume?.volume_type
        },
      },
      {
        id: 'bootable',
        label: (
          <TitleWithInfoIcon
            titleText="Bootable"
            tooltipMsg={<div>This information is inherited from the source volume</div>}
          />
        ),
        render: (_, item) => {
          const volume = volumesById?.[item?.volume_id]
          return volume?.bootable
        },
      },
      {
        id: 'created_at',
        label: 'Created',
        render: (value) => formatDate(value),
      },
    ],
    [volumesById],
  )

  const disableMultipleVMs = useMemo(() => {
    if (params.sourceType === 'existingVolume')
      return {
        disabled: true,
        message: 'Bulk VM Creation is not possible if VM is created from existing volume  ',
      }

    if (params.attachVolumes.length > 0)
      return {
        disabled: true,
        message: 'Bulk VM Creation is not possible if additional volumes are attached to the VM',
      }

    if (params?.networks?.some((network) => params?.selectedPorts[network.id]))
      return {
        disabled: true,
        message: 'You can create only 1 VM when a specific port is selected from a network.',
      }

    return {
      disabled: false,
      message: '',
    }
  }, [params?.networks, params?.selectedPorts, params?.attachVolumes])

  useEffect(() => {
    if (showVmPassword) {
      const modalBody = document.querySelector('.modal-body')
      modalBody.scrollTo(0, 0)
    }
  }, [showVmPassword])

  return (
    <ModalForm
      route={addRoute}
      title={modalTitle}
      onSubmit={showVmPassword ? handleClose : submitForm}
      onClose={handleClose}
      submitting={updating || creatingMultiple}
      steps={returnStepNumber(params.sourceType, 5)}
      error={
        responseError?.message === AVAILABLITY_ZONE_ERROR
          ? {
              message: `The selected cluster '${params.availabilityZone}' does not have any hosts assigned. Please assign a host to it or select a different cluster`,
            }
          : responseError
      }
      submitTitle={showVmPassword ? `Finish` : `Deploy Virtual Machine`}
      hideBack={showVmPassword}
      maxWidth={1300}
      showConfirmationOnCancel
    >
      {({ step }) => (
        <>
          {!showVmPassword ? (
            <>
              {step === 1 && (
                <div className={classes.fields}>
                  <ServiceUnhealthyInfo action="VM Creation" hypervisor imageLibrary blockStorage />
                  {['image', 'snapshot'].includes(params.sourceType) && (
                    <Info className={classes.warning} error>
                      {VOLUME_BACKED_VM_WARNING_MSG}
                    </Info>
                  )}
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Basic VM Info</Text>
                      <Text variant="body2">
                        Create a new virtual machine by selecting the desired image and target
                        cluster.
                      </Text>
                    </div>
                  </Info>
                  <div className={classes.halfColumns}>
                    <TextField
                      id="name"
                      label="Name"
                      onChange={getParamsUpdater('name')}
                      value={params.name}
                      info="Choose a name for your VM"
                      required
                    />
                    <PicklistField
                      id="availabilityZone"
                      label="Cluster"
                      info="Choose a cluster for your VM"
                      onChange={getParamsUpdater('availabilityZone')}
                      value={params.availabilityZone}
                      disableClusterWithoutHosts
                      fullWidth
                      required={isVmware && isOpenCloud() ? false : true}
                      DropdownComponent={ClusterHostGroupsPicklist}
                    />
                  </div>
                  <div className={classes.halfColumns}>
                    <PicklistField
                      DropdownComponent={SourceTypesPicklist}
                      id="sourceType"
                      label="Boot VM From"
                      onChange={(value) =>
                        updateParams({
                          sourceType: value,
                          image: [],
                          attachVolumes: [],
                          volume: [],
                        })
                      }
                      value={params.sourceType}
                      required
                    />
                  </div>
                  {params.sourceType === 'volumeSnapshot' && (
                    <ToggleSwitchField
                      id="deleteOnTermination"
                      value={params.deleteOnTermination}
                      onChange={getParamsUpdater('deleteOnTermination')}
                      label="Delete Volume on VM Termination"
                      tooltipMsg="If enabled, the VM boot volume will be deleted when the VM is deleted. If disabled, the volume will remain after the VM is deleted."
                    />
                  )}
                  {['image', 'snapshot', 'newVolume'].includes(params.sourceType) && (
                    <>
                      {params.sourceType === 'newVolume' && (
                        <>
                          <div className={classes.halfColumns}>
                            <TextField
                              id="volumeSize"
                              label="Volume Size (GiB)"
                              onChange={(value) => {
                                getParamsUpdater('volumeSize')(value)
                                if (
                                  !isImageCompatibleWithVolumeAndFlavor(params.image, {
                                    ...params,
                                    volumeSize: value,
                                  })
                                ) {
                                  updateParams({ image: [] })
                                }
                              }}
                              value={params.volumeSize}
                              info="Size of the primary VM volume"
                              type="number"
                              min={0}
                              integer
                              required
                            />
                            <PicklistField
                              id="volumeType"
                              DropdownComponent={VolumeTypesPicklist}
                              value={params.volumeType}
                              onChange={getParamsUpdater('volumeType')}
                              required
                            />
                          </div>
                          <ToggleSwitchField
                            id="deleteOnTermination"
                            value={params.deleteOnTermination}
                            onChange={getParamsUpdater('deleteOnTermination')}
                            label="Delete Volume on VM Termination"
                            tooltipMsg="If enabled, the VM boot volume will be deleted when the VM is deleted. If disabled, the volume will remain after the VM is deleted."
                          />
                        </>
                      )}
                      <div className={classes.separator} />
                      <Text variant="subtitle2">Image</Text>
                      {isImageLibraryHostAvailable ? (
                        <>
                          <Text variant="body2">Only active images will be shown below.</Text>
                          <Progress overlay loading={loadingResmgrHosts} message="Loading...">
                            {viewImageToggle === 'grid' && (
                              <PaginatedSelectableCards
                                renderSelectableCardComponent={renderImageCard}
                                data={filteredImages}
                                searchProps={['name', 'disk_format']}
                                pluralLabel="Images"
                                extraHeaderContent={
                                  <OptionsToggler
                                    options={[
                                      {
                                        icon: 'list',
                                        value: 'list',
                                      },
                                      {
                                        icon: 'grid',
                                        value: 'grid',
                                      },
                                    ]}
                                    value={viewImageToggle}
                                    onChange={setViewImageToggle}
                                  />
                                }
                                sortDropdown={
                                  <Dropdown
                                    name="sort"
                                    value={imageSort}
                                    items={[
                                      { label: 'Select...', value: '' },
                                      { label: 'Name', value: 'name' },
                                      { label: 'Virtual Size', value: 'virtualSize' },
                                    ]}
                                    onChange={(value) => setImageSort(value)}
                                  ></Dropdown>
                                }
                              />
                            )}
                            {viewImageToggle === 'list' && (
                              <>
                                <div className={classes.spaceBelow}>
                                  <OptionsToggler
                                    options={[
                                      {
                                        icon: 'list',
                                        value: 'list',
                                      },
                                      {
                                        icon: 'grid',
                                        value: 'grid',
                                      },
                                    ]}
                                    value={viewImageToggle}
                                    onChange={setViewImageToggle}
                                  />
                                  {params.sourceType === 'snapshot' && (
                                    <Dropdown
                                      label="Filter By Type:"
                                      compact
                                      showAll
                                      allKey=""
                                      items={[
                                        {
                                          value: 'VM Ephemeral Disk Snapshot',
                                        },
                                        {
                                          value: 'VM Volume Snapshot',
                                        },
                                      ]}
                                      value={imageType}
                                      onChange={(val) => setImageType(val)}
                                    />
                                  )}
                                </div>
                                <ListTableField
                                  id="image"
                                  data={filteredImages}
                                  loading={loadingImages}
                                  columns={
                                    params.sourceType === 'snapshot'
                                      ? vmSnapshotColumns
                                      : imageColumnsLocal
                                  }
                                  visibleColumns={
                                    params.sourceType === 'snapshot'
                                      ? visibleVmSnapshotColumns
                                      : visibleImageColumns
                                  }
                                  onChange={getParamsUpdater('image')}
                                  checkboxCond={(image) =>
                                    !isImageCompatibleWithVolumeAndFlavor(image, params)
                                  }
                                  checkboxCondMessage="Image size is larger than the volume size specified"
                                  value={params.image}
                                  uniqueIdentifier="id"
                                  searchTargets={['name', 'disk_format', 'imageTypeDisplay']}
                                  noScroll
                                  required
                                />
                              </>
                            )}
                          </Progress>
                        </>
                      ) : (
                        <ErrorText>No image library found. Choose a different source</ErrorText>
                      )}
                    </>
                  )}
                  {params.sourceType === 'existingVolume' && (
                    <>
                      <div className={classes.separator} />
                      <Text variant="subtitle2">Volume</Text>
                      <Text variant="body2">
                        Only <b>bootable</b> volumes with <b>available</b> status may be selected
                        below.
                      </Text>
                      <ListTableField
                        id="volume"
                        data={volumesList}
                        loading={loadingVolumes}
                        columns={volumeColumns}
                        onChange={getParamsUpdater('volume')}
                        value={params.volume}
                        uniqueIdentifier="id"
                        searchTargets={['name', 'status', 'volume_type', 'description']}
                        checkboxCond={(volume) =>
                          volume.status === 'available' && volume.bootable === 'true'
                        }
                        required
                      />
                    </>
                  )}
                  {params.sourceType === 'volumeSnapshot' && (
                    <>
                      <div className={classes.separator} />
                      <Text variant="subtitle2">Volume Snapshot</Text>
                      <Text variant="body2">
                        Only <b>bootable</b> volumes snapshots with <b>available</b> status may be
                        selected below.
                      </Text>
                      <ListTableField
                        id="volumeSnapshot"
                        data={volumeSnapshots}
                        loading={loadingVolumeSnapshots || loadingVolumes}
                        columns={volumeSnapshotColumns}
                        onChange={getParamsUpdater('volumeSnapshot')}
                        value={params.volumeSnapshot}
                        uniqueIdentifier="id"
                        searchTargets={['name', 'description', 'status']}
                        checkboxCond={(volumeSnapshot) =>
                          volumeSnapshot.status === 'available' &&
                          volumesById[volumeSnapshot?.volume_id]?.bootable === 'true'
                        }
                        required
                      />
                    </>
                  )}
                </div>
              )}
              {step === 2 && !['image', 'snapshot'].includes(params.sourceType) && (
                <div className={classes.fields}>
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Attach Additional Volumes</Text>
                      <Text variant="body2">
                        Only volumes with <b>available</b> status may be selected below.
                      </Text>
                    </div>
                  </Info>
                  <Text variant="subtitle2">Volume</Text>
                  <ListTableField
                    id="volume"
                    data={availableVolumes}
                    loading={loadingVolumes}
                    columns={volumeColumns}
                    onChange={getParamsUpdater('attachVolumes')}
                    value={params.attachVolumes}
                    uniqueIdentifier="id"
                    searchTargets={['name', 'status', 'volume_type', 'description']}
                    emptyText="No available volumes found"
                    multiSelection
                  />
                </div>
              )}
              {step === returnStepNumber(params.sourceType, 3) && (
                <div className={classes.fields}>
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Flavor Selection</Text>
                      <Text variant="body2">
                        Choose a flavor that defines the VM’s CPU, memory, disk, and optional GPU
                        settings.
                      </Text>
                    </div>
                  </Info>
                  <div /> {/* extra spacing */}
                  <div className={classes.filters}>
                    <ToggleSwitchField
                      id="enableHotplugging"
                      value={params.enableHotplugging}
                      onChange={(val) => {
                        updateParams({
                          enableHotplugging: val,
                          flavor: [],
                        })
                      }}
                      label="Hot-plug Compatible"
                    />
                    <ToggleSwitchField
                      id="gpuCompatible"
                      value={params.gpuCompatible}
                      onChange={(val) => {
                        updateParams({
                          gpuCompatible: val,
                          flavor: [],
                        })
                      }}
                      label="GPU Compatible"
                    />
                  </div>
                  {isVolumeSourceType && (
                    <Alert
                      variant="primary"
                      title="Supported Flavors"
                      message="Only zero-disk flavors are supported for volume-backed instances, as the root disk comes from the attached volume."
                    />
                  )}
                  {viewFlavorToggle === 'grid' && (
                    <PaginatedSelectableCards
                      renderSelectableCardComponent={renderFlavorCard}
                      data={filteredFlavors}
                      searchProps={['name']}
                      pluralLabel="Flavors"
                      extraHeaderContent={
                        <OptionsToggler
                          options={[
                            {
                              icon: 'list',
                              value: 'list',
                            },
                            {
                              icon: 'grid',
                              value: 'grid',
                            },
                          ]}
                          value={viewFlavorToggle}
                          onChange={setViewFlavorToggle}
                        />
                      }
                      sortDropdown={
                        <Dropdown
                          name="sort"
                          value={flavorSort}
                          items={[
                            { label: 'Select...', value: '' },
                            { label: 'Name', value: 'name' },
                            { label: 'vCPUs', value: 'vcpus' },
                            { label: 'RAM', value: 'ram' },
                            { label: 'Disk', value: 'disk' },
                          ]}
                          onChange={(value) => setFlavorSort(value)}
                        ></Dropdown>
                      }
                    />
                  )}
                  {viewFlavorToggle === 'list' && (
                    <>
                      <div className={classes.spaceBelow}>
                        <OptionsToggler
                          options={[
                            {
                              icon: 'list',
                              value: 'list',
                            },
                            {
                              icon: 'grid',
                              value: 'grid',
                            },
                          ]}
                          value={viewFlavorToggle}
                          onChange={setViewFlavorToggle}
                        />
                      </div>
                      <ListTableField
                        id="flavor"
                        data={filteredFlavors}
                        loading={loadingFlavors}
                        columns={flavorColumns}
                        onChange={getParamsUpdater('flavor')}
                        checkboxCond={(flavor) =>
                          !isFlavorCompatibleWithImageAndVolume(flavor, params)
                        }
                        checkboxCondMessage="Flavor disk size is smaller than the selected image"
                        value={params.flavor}
                        uniqueIdentifier="id"
                        searchTargets={['name']}
                        noScroll
                        required
                        emptyText={
                          isVolumeSourceType && filteredFlavors?.length === 0
                            ? 'No flavors with zero disk size are available. Please create a zero-disk-size flavor to proceed'
                            : 'No data found'
                        }
                      />
                    </>
                  )}
                  {params.enableHotplugging && (
                    <>
                      <div className={classes.separator} />
                      <div className={classes.halfColumns}>
                        <TextField
                          id="hotplugMemory"
                          label="Hotplug RAM (MB)"
                          onChange={(val) => updateParams({ hotplugMemory: val })}
                          value={params.hotplugMemory}
                          type="number"
                          min={1}
                          info="The memory that will be hotplugged to the VM. Must be less than the maximum."
                          validations={[hotplugMemoryValidator, nonDecimalValueValidator()]}
                          required
                        />
                        <TextField
                          id="hotplugMemoryMax"
                          label="Hotplug RAM Maximum (MB)"
                          onChange={(val) => updateParams({ hotplugMemoryMax: val })}
                          value={params.hotplugMemoryMax}
                          type="number"
                          min={1}
                          info="The maximum amount of RAM that can be hotplugged to the VM."
                          required
                          validations={[nonDecimalValueValidator()]}
                        />
                      </div>
                      <div className={classes.halfColumns}>
                        <TextField
                          id="hotplugVcpu"
                          label="Hotplug vCPUs"
                          onChange={(val) => updateParams({ hotplugVcpu: val })}
                          value={params.hotplugVcpu}
                          type="number"
                          min={1}
                          info="The number of vCPUs that will be hotplugged to the VM. Must be less than the maximum."
                          validations={[hotplugVcpuValidator, nonDecimalValueValidator()]}
                          required
                        />
                        <TextField
                          id="hotplugVcpuMax"
                          label="Hotplug vCPUs Maximum"
                          onChange={(val) => updateParams({ hotplugVcpuMax: val })}
                          value={params.hotplugVcpuMax}
                          type="number"
                          min={1}
                          info="The maximum number of vCPUs that can be hotplugged to the VM."
                          validations={[hotplugVcpuMaxValidator, nonDecimalValueValidator()]}
                          required
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
              {step === returnStepNumber(params.sourceType, 4) && (
                <div className={classes.fields}>
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Networking</Text>
                      <Text variant="body2">
                        Assign a network to the VM by selecting from available network pools.
                      </Text>
                    </div>
                  </Info>
                  <ListTableField
                    id="networks"
                    data={networksList}
                    loading={loadingNetworks || loadingNetworkAvailability}
                    columns={networkColumns}
                    onChange={(selectedNetworks) => {
                      setMinAvailableIpsNetwork(null)
                      getParamsUpdater('networks')(selectedNetworks)
                    }}
                    value={params.networks}
                    uniqueIdentifier="id"
                    searchTargets={['name']}
                    multiSelection
                    noScroll
                    required
                    checkboxCond={(network) => network?.availablePortAndIPs > 0}
                    checkboxCondMessage={'Network has no available IP addresses or ports'}
                  />
                </div>
              )}
              {step === returnStepNumber(params.sourceType, 5) && (
                <div className={classes.fields}>
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Customize VM</Text>
                      <Text variant="body2">
                        Set advanced options like SSH keys, user data, security groups, VM metadata
                        and optional GPU lease time.
                      </Text>
                    </div>
                  </Info>

                  {minAvailableIpsNetwork?.availablePortAndIPs < params.count && (
                    <Alert
                      variant="warning"
                      message={
                        <Text variant="body2">
                          Not enough available IPs or ports. Network
                          <Text variant="caption1"> {minAvailableIpsNetwork?.name} </Text>
                          only contains {minAvailableIpsNetwork?.availablePortAndIPs} available IPs
                          or ports. Please select another network or select VM count between 1 and{' '}
                          {minAvailableIpsNetwork?.availablePortAndIPs}.
                        </Text>
                      }
                    />
                  )}
                  {disableMultipleVMs.disabled && (
                    <Alert
                      variant="warning"
                      message={<Text variant="body2">{disableMultipleVMs.message}</Text>}
                    />
                  )}
                  <Text variant="subtitle2">VM Spec</Text>
                  <div>
                    <Text variant="body2">Number of VMs</Text>
                    <div
                      className={clsx(classes.vmCount, {
                        [classes.vmCountDisabled]: disableMultipleVMs.disabled,
                      })}
                    >
                      <Slider
                        min={1}
                        max={disableMultipleVMs.disabled ? 1 : 20}
                        minLabel="1 VM"
                        maxLabel="20 VMs"
                        step={1}
                        onChange={(val) => updateParams({ count: val })}
                        value={params.count}
                      />
                      <div className={classes.vmCountInput}>
                        <TextField
                          id="count"
                          label=""
                          onChange={(val) => updateParams({ count: val })}
                          value={params.count}
                          type="number"
                          min={1}
                          max={20}
                          disabled={disableMultipleVMs.disabled}
                          validations={[countValidator]}
                          required
                        />
                        <Text variant="body2" className={classes.vmCountText}>
                          VMs
                        </Text>
                      </div>
                    </div>
                  </div>
                  <div className={classes.halfColumns}>
                    <PicklistField
                      id="sshKey"
                      DropdownComponent={SshKeyPicklist}
                      value={params.sshKey}
                      onChange={getParamsUpdater('sshKey')}
                    />
                    <PicklistField
                      id="serverGroup"
                      DropdownComponent={ServerGroupsPicklist}
                      value={params.serverGroup}
                      onChange={getParamsUpdater('serverGroup')}
                    />
                  </div>
                  <div className={classes.separator} />
                  <ToggleSwitchField
                    id="useCloudInit"
                    value={params.useCloudInit}
                    onChange={getParamsUpdater('useCloudInit')}
                    label="Use Cloud-init"
                    textDescription={
                      'Add user data for initial VM setup, such as installing packages, or configuring users. This will run on first boot.'
                    }
                  />
                  {params.useCloudInit && (
                    <div className={classes.tabbed}>
                      <CodeMirror
                        variant="light"
                        label="Cloud-init YAML"
                        id="cloudInitYaml"
                        onChange={getParamsUpdater('cloudInitYaml')}
                        value={params.cloudInitYaml}
                        validations={[yamlValidator]}
                      />
                    </div>
                  )}
                  <ToggleSwitchField
                    id="useSecurityGroups"
                    value={params.useSecurityGroups}
                    onChange={getParamsUpdater('useSecurityGroups')}
                    label="Assign Security Groups"
                    textDescription={
                      'It applies a Security Group at the VM level to ensure consistent traffic rules across all network interfaces attached to the VM.'
                    }
                  />
                  {params.useSecurityGroups && (
                    <div className={classes.tabbed}>
                      <ListTableField
                        id="securityGroups"
                        data={securityGroupsList}
                        loading={loadingSecurityGroups}
                        columns={securityGroupColumns}
                        onChange={getParamsUpdater('securityGroups')}
                        value={params.securityGroups}
                        uniqueIdentifier="id"
                        searchTargets={['name']}
                        multiSelection
                        required
                      />
                    </div>
                  )}
                  <div className={classes.separator} />
                  <Text variant="subtitle2">Set Migration Priority</Text>
                  <Text variant="body2">
                    The migration priority determines whether the virtual machine qualifies as a
                    candidate for migration by the DRR service, and, if it does, specifies the
                    priority level for its migration.{' '}
                    <ExternalLink
                      url={
                        'https://platform9.com/docs/private-cloud-director/private-cloud-director/dynamic-resource-rebalancing--drr-#drr-and-vm-migration-priority'
                      }
                    >
                      More info
                    </ExternalLink>
                  </Text>
                  <PicklistField
                    id="priority"
                    DropdownComponent={MigrationPriorityPicklist}
                    value={params.priority}
                    onChange={getParamsUpdater('priority')}
                  />
                  <div className={classes.separator} />
                  <KeyValuesField
                    id="metadata"
                    label="Metadata"
                    value={params.metadata}
                    onChange={getParamsUpdater('metadata')}
                    addLabel="Add Metadata"
                    allowMultipleValues
                  />
                </div>
              )}
            </>
          ) : (
            <div className={classes.passwordScreen}>
              <VmCredentialsPanel vmPassword={vmPassword} />
            </div>
          )}
        </>
      )}
    </ModalForm>
  )
}
